import {
  SeverityBadge,
  MatchRateBadge,
  StatusDistributionBar,
  ChartLegend,
  ChartTooltip,
  LiveProgressIndicator,
} from "../components";
import {
  SeverityTier,
  SEVERITY_TIER_ORDER,
  SEVERITY_TIER_CONFIG,
} from "../types/severity-tier";
import type { StatusBreakdown } from "../types/shadow-run";
import styles from "./ComponentShowcaseScreen.module.css";

const sampleBreakdown: StatusBreakdown[] = [
  { tier: SeverityTier.MATCH, count: 450, percentage: 45 },
  { tier: SeverityTier.ACCEPTABLE, count: 200, percentage: 20 },
  { tier: SeverityTier.MINOR, count: 150, percentage: 15 },
  { tier: SeverityTier.VARIANCE, count: 100, percentage: 10 },
  { tier: SeverityTier.CRITICAL, count: 50, percentage: 5 },
  { tier: SeverityTier.ERROR, count: 30, percentage: 3 },
  { tier: SeverityTier.SKIPPED_ZERO, count: 15, percentage: 1.5 },
  { tier: SeverityTier.SKIPPED_REVERSAL, count: 5, percentage: 0.5 },
];

const highMatchBreakdown: StatusBreakdown[] = [
  { tier: SeverityTier.MATCH, count: 980, percentage: 98 },
  { tier: SeverityTier.ACCEPTABLE, count: 15, percentage: 1.5 },
  { tier: SeverityTier.MINOR, count: 5, percentage: 0.5 },
];

const legendItems = [
  { label: "Match", color: SEVERITY_TIER_CONFIG[SeverityTier.MATCH].color, value: 450 },
  { label: "Acceptable", color: SEVERITY_TIER_CONFIG[SeverityTier.ACCEPTABLE].color, value: 200 },
  { label: "Minor", color: SEVERITY_TIER_CONFIG[SeverityTier.MINOR].color, value: 150 },
  { label: "Variance", color: SEVERITY_TIER_CONFIG[SeverityTier.VARIANCE].color, value: 100 },
  { label: "Critical", color: SEVERITY_TIER_CONFIG[SeverityTier.CRITICAL].color, value: 50 },
];

export function ComponentShowcaseScreen() {
  return (
    <div className={styles.page} data-testid="showcase-page">
      <h1 className={styles.title}>Shadow Runs Component Showcase</h1>
      <p className={styles.subtitle}>
        Dev-only page for visual verification of shared visualization components
      </p>

      {/* Severity Badges */}
      <section className={styles.section} data-testid="severity-badges-section">
        <h2 className={styles.sectionTitle}>Severity Badges</h2>
        <div className={styles.grid}>
          {SEVERITY_TIER_ORDER.map((tier) => (
            <SeverityBadge key={tier} tier={tier} />
          ))}
        </div>
      </section>

      {/* Match Rate Badges */}
      <section className={styles.section} data-testid="match-rate-badges-section">
        <h2 className={styles.sectionTitle}>Match Rate Badges</h2>
        <div className={styles.grid}>
          <MatchRateBadge rate={99.5} />
          <MatchRateBadge rate={96.2} />
          <MatchRateBadge rate={87.3} />
          <MatchRateBadge rate={72.1} />
        </div>
      </section>

      {/* Status Distribution Bars */}
      <section className={styles.section} data-testid="distribution-bar-section">
        <h2 className={styles.sectionTitle}>Status Distribution Bar</h2>
        <div className={styles.barGroup}>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>Mixed distribution (all tiers)</div>
            <StatusDistributionBar breakdown={sampleBreakdown} height={12} />
          </div>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>High match rate</div>
            <StatusDistributionBar breakdown={highMatchBreakdown} height={12} />
          </div>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>Empty state</div>
            <StatusDistributionBar breakdown={[]} height={12} />
          </div>
        </div>
      </section>

      {/* Chart Legend */}
      <section className={styles.section} data-testid="chart-legend-section">
        <h2 className={styles.sectionTitle}>Chart Legend</h2>
        <div>
          <div className={styles.barLabel}>Horizontal</div>
          <ChartLegend items={legendItems} direction="horizontal" />
        </div>
        <div className={styles.legendBlock}>
          <div className={styles.barLabel}>Vertical</div>
          <ChartLegend items={legendItems} direction="vertical" />
        </div>
      </section>

      {/* Live Progress Indicator */}
      <section className={styles.section} data-testid="live-progress-section">
        <h2 className={styles.sectionTitle}>Live Progress Indicator</h2>
        <div className={styles.barGroup}>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>Running (with match rate)</div>
            <LiveProgressIndicator status="running" matchRate={72.5} />
          </div>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>Running (no match rate)</div>
            <LiveProgressIndicator status="running" />
          </div>
          <div className={styles.barContainer}>
            <div className={styles.barLabel}>Pending</div>
            <LiveProgressIndicator status="pending" />
          </div>
        </div>
      </section>

      {/* Chart Tooltip */}
      <section className={styles.section} data-testid="chart-tooltip-section">
        <h2 className={styles.sectionTitle}>Chart Tooltip</h2>
        <div className={styles.tooltipDemo}>
          <ChartTooltip top={20} left={120}>
            <div>
              <strong>Match Rate: 98.5%</strong>
              <div>450 packets matched</div>
            </div>
          </ChartTooltip>
          <span>Tooltip rendered at fixed position</span>
        </div>
      </section>
    </div>
  );
}
